import { publicSearchApi } from '@envato/foundation-types'
import get from 'lodash-es/get'
import head from 'lodash-es/head'
import keys from 'lodash-es/keys'
import last from 'lodash-es/last'
import startCase from 'lodash-es/startCase'
import naturalSort from 'natsort'
import { formatNumberWithCommas } from '../../../../common/utils/formatString.js'

export interface IStringLookup {
  [key: string]: string
}

const SpecialCaseLabelFormats: IStringLookup = {
  'minimum-adobe-cs-version': 'Minimum Adobe CS Version',
  'compatible-os': 'Compatible OS',
  't-shirt-size': 'T-Shirt Size',
  'compatible-software': 'Software Version',
  '3d-software-package': '3D Software Package'
}

const AttributesToInclude: string[] = [
  'compatible-with',
  'compatible-software',
  'software-framework',
  'application-runtime',
  'operating-system-versions',
  'opens-with',
  'layered',
  'works-with',
  'minimum-adobe-cs-version',
  'print-dimensions',
  'pixel-dimensions',
  'resolution',
  'animated',
  'poly-count',
  '3d-software-package'
]

const attributesAsRanges: string[] = ['compatible-software', 'compatible-with']
const attributesAsLargeNumbers: string[] = ['poly-count']

export const groupVersionsByName = (versions: string[]): any => {
  return versions.reduce((acc: any, curr: string) => {
    curr = curr.trim()

    if (curr === '') {
      return acc
    }

    const matched = curr.match(/(^\D*)\s*(\d*.*)/)

    if (matched !== null) {
      const name = matched[1].trim()
      const version = matched[2].trim()
      const group = get(acc, name, [])

      if (version.includes('-')) {
        const versionRange = version.split('-')
        const minVersion = versionRange[0].trim()
        const maxVersion = versionRange[1].trim()

        return { ...acc, [name]: [...group, minVersion, maxVersion] }
      }

      return { ...acc, [name]: [...group, version] }
    } else {
      return acc
    }
  }, {})
}

export const getVersionRange = (versions: string[]) => {
  const groupedByName = groupVersionsByName(versions)
  const names = keys(groupedByName)

  return names.map(name => {
    const software = groupedByName[name].sort(
      naturalSort({ desc: false })
    ) as string[]
    const specialCasing = {
      Other: 'Other'
    } as any

    if (keys(specialCasing).includes(name)) {
      return specialCasing[name]
    }

    if (!software.length) {
      return name
    }

    if (software.length === 1) {
      return `${name} ${head(software)}`.trim()
    }

    return `${name} ${head(software)} - ${last(software)}`
  })
}

export const formattedLabel = (attr: publicSearchApi.IAttribute) =>
  SpecialCaseLabelFormats[attr.name] || startCase(attr.name)

export const formattedValue = (attr: publicSearchApi.IAttribute) => {
  if (attributesAsLargeNumbers.includes(attr.name)) {
    return formatNumberWithCommas(attr.value)
  }

  if (!Array.isArray(attr.value)) {
    return attr.value
  }

  return attributesAsRanges.includes(attr.name)
    ? getVersionRange(attr.value).join(', ')
    : attr.value.join(', ')
}

const shouldExcludeAttribute = (attr: publicSearchApi.IAttribute) =>
  attr.value === null ||
  attr.value === 'n/a' ||
  attr.value === 'N/A' ||
  !AttributesToInclude.includes(attr.name)

export const filterAttributes = (attributes: publicSearchApi.IAttribute[]) =>
  attributes.filter(attr => !shouldExcludeAttribute(attr))
